<template lang="pug">
    div
        div.dashboard-nav-bar.l-flex-row
            div.dashboard-nav.l-flex-row
                div.nav-item(:class="{'is-active': activeTab === 'account'}" @click="setTab('account', false)") Account
                div.nav-item(:class="{'is-active': activeTab === 'audiences'}" @click="setTab('audiences', false)") Top Audiences
                div.nav-item(:class="{'is-active': activeTab === 'segments'}" @click="setTab('segments', false)") Top Segments
                div.nav-item(:class="{'is-active': activeTab === 'alerts'}" @click="setTab('alerts', false)") Alerts
     
            div.l-flex-row
                div.dashboard-nav-recency
                    el-date-picker(
                        v-if="activeTab !== 'alerts'"
                        v-model="recencyValue"
                        name="end_date"
                        editable=true
                        clearable=false
                        type="daterange"
                        format="yyyy-MM-dd"
                        value-format="yyyy-MM-dd"
                        :picker-options="endDateOptions"
                        v-validate="'date_format:YYYY-MM-DD|required'"
                        placeholder="Transfer end date")
                div.btn.btn--medium.btn--round.btn-rev(v-if="isAlertCreationAllowed" @click="toggleAlertForm(true)") Create Alert
        
        div
            AccountTab(
                v-if="activeTab === 'account'"
                :loading="isLoading"
                :allocationData="allocationData"
                :allocationTimeUnit="currentAllocationTimeUnit"
                :kibanaData="kibanaData"
            )
            
            KeepAlive
                AllocationTable(
                    v-if="activeTab === 'audiences'"
                    rowName="audience"
                    :loading="topTableLoading"
                    :allocations="allocationTopAudiences"
                    :allocationsDetails="audiences"
                    :alerts="audienceAlertsByObjectId"
                    @expandRow="getAudienceAllocation"
                    @goToAlertTab="goToAlertTab"
                    key="audiences"
                )

                AllocationTable(
                    v-if="activeTab === 'segments'"
                    rowName="segment"
                    :showDeleted="false"
                    :loading="topTableLoading"
                    :allocations="allocationTopSegments"
                    :allocationsDetails="segments",
                    :alerts="segmentAlertsByObjectId"
                    @expandRow="getSegmentAllocation"
                    key="cs"
                )
        div(v-if="activeTab === 'alerts'")
            Alerts()

        //- Alert Form
        div.overlay-close(v-if="displayAlertForm" @click="toggleAlertForm(false)")
        div.modal.modal-h-full.modal__medium.modal-cclone(v-if="displayAlertForm")
            AlertForm()

</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import { groupBy, isEmpty, keyBy } from 'lodash'
import WamSelect from '@/components/common/WamSelect/WamSelect'
import AccountTab from '@/components/dashboard/AccountTab'
import AllocationTable from '@/components/dashboard/AllocationTable'
import Alerts from '@/components/dashboard/Alerts'
import AlertForm from '@/components/dashboard/AlertForm.vue'
import dayjs from 'dayjs'

export default {
    name: 'Allocation',

    data() {
        const defaultEnDate = dayjs()
        const defaultstartDate = dayjs().subtract(7, 'days')
        const dateRange = [defaultstartDate.toISOString(), defaultEnDate.toISOString()]

        return {
            isLoading: false,
            recencyValue: dateRange,
            defaultDateRange: dateRange
        }
    },

    components: {
        AccountTab,
        AllocationTable,
        WamSelect,
        AlertForm,
        Alerts
    },

    watch: {
        recencyValue: {
            handler(val) {
                if (val === null) this.recencyValue = this.defaultDateRange
                else {
                    this.setSelectedTimeRange({ dateRange: val })
                    this.resetData()
                    this.loadData()
                }
            },
            immediate: true
        }
    },

    computed: {
        ...mapState('dashboard', {
            activeTab: state => state.activeTab,
            selectedTimeRange: state => state.selectedTimeRange,
            currentAllocationTimeUnit: state => state.currentAllocationTimeUnit,
            allocationTopLoading: state => state.allocationTop.loading,
            // Account
            allocationData: state => state.allocationData,
            kibanaData: state => state.kibanaData,
            displayAlertForm: state => state.displayAlertForm,
            // Alerts
            alerts: state => Object.values(state.alerts),
            alertsLoading: state => state.alertsLoading,
            // Audiences
            allocationTopAudiences: state => state.allocationTop.audiences,
            // Segments
            allocationTopSegments: state => state.allocationTop.segments
        }),

        ...mapState('data/audience', {
            audiences: state => keyBy(state.list, 'id')
        }),

        ...mapState('data/customTaxonomies', {
            segments: state => keyBy(Object.values(state.hierarchies).flat(), 'id')
        }),

        ...mapState('account', {
            accountId: state => state.accountId
        }),

        ...mapGetters('dashboard', ['isKibanaAllowed']),
        ...mapGetters('account', ['getCreateAlertsAllowed']),

        endDateOptions() {
            const maxStartDate = new Date().setDate(new Date().getDate() - 31)
            return {
                disabledDate(time) {
                    const now = new Date()
                    return !(time.getTime() < now && time.getTime() >= maxStartDate)
                }
            }
        },

        topTableLoading() {
            return this.allocationTopLoading || this.alertsLoading
        },

        audienceAlertsByObjectId() {
            const alerts = this.alerts.filter(a => a.objectType == 'audience')
            return groupBy(alerts, 'objectId')
        },

        segmentAlertsByObjectId() {
            const alerts = this.alerts.filter(a => a.objectType == 'cs')
            return groupBy(alerts, 'objectId')
        },

        isAlertCreationAllowed() {
            return this.getCreateAlertsAllowed()
        }
    },

    methods: {
        ...mapMutations('dashboard', [
            'setActiveTab',
            'setSelectedTimeRange',
            'setAllocationData',
            'setAllocationTopAudiences',
            'setDisplayAlertForm',
            'setAllocationTop'
        ]),

        ...mapActions('dashboard', ['getAlerts', 'getAllAllocations', 'getTop', 'getAllocationsBy', 'getKibanaData']),

        async setTab(tab, withQuery) {
            if (!withQuery && (this.$route?.query?.query || this.$route?.query?.queryType)) {
                this.$router.replace({ query: null })
            }
            this.setActiveTab({ tab: tab })
            await this.$nextTick()
            if (tab !== 'alerts') this.loadData()
        },

        loadData() {
            const _actions = {
                account: {
                    fetchFn: this.retrieveAllocation,
                    data: this.allocationData
                },
                audiences: {
                    fetchFn: () => this.retrieveTop('audiences'),
                    data: this.allocationTopAudiences
                },
                segments: {
                    fetchFn: () => this.retrieveTop('segments'),
                    data: this.allocationTopSegments
                }
            }

            if (isEmpty(_actions[this.activeTab]?.data)) {
                _actions[this.activeTab].fetchFn()
            }
        },

        resetData() {
            this.setAllocationData({ data: [] })
            this.setAllocationTop({ type: 'audiences', data: {} })
            this.setAllocationTop({ type: 'segments', data: {} })
        },

        async retrieveAllocation() {
            this.isLoading = true
            await this.getAllAllocations({
                start: this.recencyValue[0],
                end: this.recencyValue[1]
            })
            if (this.isKibanaAllowed) {
                await this.getKibanaData({
                    start: this.recencyValue[0],
                    end: this.recencyValue[1]
                })
            }
            this.isLoading = false
        },

        retrieveTop(type) {
            if (isEmpty(this.alerts)) this.getAlerts()
            this.getTop({ type })
        },

        toggleAlertForm(value) {
            if (value) {
                this.getTop({ type: 'audiences' })
                this.getTop({ type: 'segments' })
            }
            this.setDisplayAlertForm({ value: value })
        },

        async getAudienceAllocation({ id, allocation }) {
            if (allocation > 0) {
                await this.getAllocationsBy({ type: 'audiences', id })
            }
        },

        async getSegmentAllocation({ id, allocation }) {
            if (allocation > 0) {
                await this.getAllocationsBy({ type: 'segments', id })
            }
        },

        goToAlertTab({id, triggered}) {
            this.setTab('alerts')
            const query = {
                query: id,
                queryType: 'objectId',
            }
            if (triggered) query.triggered = true
            else delete query.triggered
            this.$router.push({
                query
            }).catch((e) => e)
        }

    }
}
</script>
